<template>
  <Dialog
    id="tender-setup-dialog"
    v-model:visible="showDialog"
    :closable="false"
    :draggable="false"
    :modal="true"
    :header="
      t('sites.singleSite.financialConfig.mealPlans.form.tenders.tenderSetup.title', {
        tenderName: field.value?.name,
      })
    "
    :pt="{
      root: { class: 'w-[50%]' },
    }"
  >
    <template #default>
      <Form
        id="tender-dialog-form"
        :form-id="FinancialFormIDs.TENDER_SETUP_FORM"
        :schema="tenderSetupSchema"
        :initialValues="initialValues"
        @onValidateSuccess="submitForm"
        ref="formComponent"
      >
        <template v-slot="{ isFormValid, form }">
          <Grid>
            <Col12>
              <Grid>
                <Col6>
                  <TenderID :fieldName="'id'" :index="parentIndex" />
                </Col6>

                <Col6>
                  <TenderMaxSwipes :fieldName="'max_swipes_per_transaction'" :index="parentIndex" />
                </Col6>
              </Grid>

              <Grid>
                <Col6>
                  <TenderTaxExempt :fieldName="'tax_exempt'" :index="parentIndex" />
                </Col6>
              </Grid>

              <Grid>
                <Col12 class="mt-4">
                  <span class="text-sm font-medium">{{
                    t(
                      'sites.singleSite.financialConfig.mealPlans.form.tenders.tenderSetup.equivalencyTimeRanges',
                    )
                  }}</span>
                  <Divider class="mt-0" />
                </Col12>

                <Col12>
                  <FieldArrayValidator name="rates" :key="dummyState">
                    <template v-slot="{ fields, remove, push }">
                      <Grid>
                        <template v-for="(field, index) in fields" :key="field.key">
                          <Col3>
                            <StartTime :field-name="fieldStartTime(index)" :index="index" />
                          </Col3>

                          <Col3>
                            <EndTime :field-name="fieldEndTime(index)" :index="index" />
                          </Col3>

                          <Col3>
                            <Amount :field-name="fieldAmount(index)" :index="index" />
                          </Col3>

                          <Col3>
                            <div class="flex items-center mt-4">
                              <Transition name="fade">
                                <IconButton
                                  class="c-icon-button"
                                  :disabled="fields?.length === 1"
                                  @click="removeRate(index, remove)"
                                >
                                  <template #icon>
                                    <i class="material-symbols-rounded text-[1.25rem]"> delete </i>
                                  </template>
                                </IconButton>
                              </Transition>

                              <Transition name="fade">
                                <IconButton
                                  :id="`add-rate-${index}`"
                                  class="c-icon-button"
                                  @click="push(createNewRate())"
                                >
                                  <template #icon>
                                    <i class="material-symbols-rounded text-[1.25rem]"> add </i>
                                  </template>
                                </IconButton>
                              </Transition>
                            </div>
                          </Col3>
                        </template>
                      </Grid>
                    </template>
                  </FieldArrayValidator>
                </Col12>
              </Grid>
            </Col12>
          </Grid>
        </template>
      </Form>
    </template>

    <template #footer>
      <Button
        id="close-dialog"
        :class="ButtonSeverityClass.SECONDARY"
        :label="t('sites.singleSite.financialConfig.mealPlans.form.tenders.tenderSetup.cancel')"
        @click="closeDialog"
      />

      <Button
        id="confirm-dialog"
        :label="t('sites.singleSite.financialConfig.mealPlans.form.tenders.tenderSetup.save')"
        @click="save"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ButtonSeverityClass } from '@centric-os/components/src/core/buttons/enums';
import TenderID from '../tender-id/TenderID.vue';
import TenderTaxExempt from '../tender-tax-exempt/TenderTaxExempt.vue';
import { FinancialFormIDs } from '../../enums/enums';
import { computed, ref, type PropType } from 'vue';
import { TenderTypes, type Tender, type TenderRate } from '@centric-os/types';
import type { FieldEntry } from 'vee-validate';
import TenderMaxSwipes from '../tender-max-swipes/TenderMaxSwipes.vue';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import { Form } from '@centric-os/components/src/composed/forms/form';
import Grid from '@centric-os/components/src/core/layouts/grids/grid/Grid.vue';
import Amount from '../rates/amount/Amount.vue';
import EndTime from '../rates/end-time/EndTime.vue';
import StartTime from '../rates/start-time/StartTime.vue';
import { cloneDeep } from 'lodash-es';
import { useMealPlanSchema, useMeqUtils } from '../../../../../composables';

// Define emits
const emit = defineEmits(['save']);

const { showErrorToast } = useGlobalToast();

// Define vmodel
const showDialog = defineModel({
  type: Boolean,
  required: true,
});

// Define props
const props = defineProps({
  field: {
    type: Object as PropType<FieldEntry<Tender>>,
    required: true,
  },
  parentIndex: {
    type: Number,
    required: true,
  },
});

// Set localization
const { t } = useI18n();

const getMeqValidationFailedMessage = (error: string | undefined): string => {
  switch (error) {
    case 'missingRanges':
      return t(
        'sites.singleSite.financialConfig.mealPlans.form.tenders.tenderSetup.validationFailed.missingRanges',
      );
    case 'overlappingRanges':
      return t(
        'sites.singleSite.financialConfig.mealPlans.form.tenders.tenderSetup.validationFailed.overlappingRanges',
      );
    case 'incompleteCoverage':
    default:
      return t(
        'sites.singleSite.financialConfig.mealPlans.form.tenders.tenderSetup.validationFailed.incompleteCoverage',
      );
  }
};

// Create a dummy reactive state
const dummyState = ref(0);

// Define refs for form component
const formComponent = ref<InstanceType<typeof Form>>();

// Set tender setup form schema
const { tenderSetupSchema } = useMealPlanSchema();

// Use the MEQ utils composable
const { validateMealEquivalencyTenderTimeRanges } = useMeqUtils();

// Computed to set initial values
const initialValues = computed<Partial<Tender>>(() => {
  const initialValues = {
    id: props.field.value?.id,
    // @ts-expect-error FIXME: Property 'tax_exempt' does not exist on type 'Tender'.
    tax_exempt: props.field.value?.tax_exempt,
    // @ts-expect-error FIXME: Property 'max_swipes_per_transaction' does not exist on type 'Tender'.
    max_swipes_per_transaction: props.field.value?.max_swipes_per_transaction,
    // @ts-expect-error FIXME: Property 'rates' does not exist on type 'Tender'.
    rates: props.field.value?.rates,
  };

  return initialValues;
});

// Submit form
const save = async (): Promise<void> => {
  const values = formComponent.value?.form.values;

  // Check if this is an MEQ tender
  if (props.field.value?.type === TenderTypes.MEAL_EQUIVALENT) {
    // Validate the rate ranges using the composable
    const validation = validateMealEquivalencyTenderTimeRanges(values?.rates);
    if (!validation.isValid) {
      showErrorToast(getMeqValidationFailedMessage(validation.error));
      return; // Stop here if validation failed
    }
  }

  formComponent.value?.validate();
};

// Method to submit form to parent
const submitForm = (values: Partial<Tender>): void => {
  emit('save', cloneDeep(values));
  closeDialog();
};

// Method to close dialog
const closeDialog = (): void => {
  showDialog.value = false;
};

// Set up field names
const fieldStartTime = (index: number): string => `rates[${index}].startTime`;
const fieldEndTime = (index: number): string => `rates[${index}].endTime`;
const fieldAmount = (index: number): string => `rates[${index}].amount`;

// Method to add new rate
const createNewRate = (): Partial<TenderRate> => {
  return {
    amount: 0,
    startTime: null,
    endTime: null,
  };
};

// Remove tender
const removeRate = (index: number, remove: (index: number) => void): void => {
  remove(index);
  dummyState.value++;
};
</script>
